import type {
  GetProjectViewListResponseType,
  RequestRatingEnum,
} from "@nlc/shared-types";

import { keepPreviousData, useQuery } from "@tanstack/react-query";

import apiClient from "@/lib/api/client";

interface UseGetProjectUsersParams {
  completedAtFrom?: string;
  completedAtTo?: string;
  completedOnly?: boolean;
  designerId?: number;
  direction?: "asc" | "desc";
  itemsPerPage?: number;
  page?: number;
  projectId: number;
  rating: null | RequestRatingEnum;
  search?: string;
  sortBy?: string;
}

export const useGetProjectView = ({
  completedAtFrom,
  completedAtTo,
  completedOnly = false,
  designerId,
  direction = "desc",
  itemsPerPage = 10,
  page = 1,
  projectId,
  rating,
  search = "",
  sortBy = "createdAt",
}: UseGetProjectUsersParams) =>
  useQuery<GetProjectViewListResponseType>({
    enabled: !!projectId || designerId !== undefined,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams({
        direction,
        itemsPerPage: itemsPerPage.toString(),
        page: page.toString(),
        projectId: projectId.toString(),
        sortBy,
      });

      if (search) {
        params.append("search", search);
      }

      if (rating) {
        params.append("rating", rating);
      }

      if (completedAtFrom) {
        params.append("completedAtFrom", completedAtFrom);
      }

      if (completedAtTo) {
        params.append("completedAtTo", completedAtTo);
      }

      if (completedOnly) {
        params.append("completedOnly", "true");
      }

      if (designerId) {
        params.append("designerId", designerId.toString());
      }

      return await apiClient
        .url(`/projects/${projectId}/view?${params}`)
        .get()
        .json();
    },
    queryKey: [
      "project-users",
      projectId,
      page,
      itemsPerPage,
      search,
      sortBy,
      direction,
      rating,
      completedAtFrom,
      completedAtTo,
      completedOnly,
      designerId,
    ],
    staleTime: 30_000,
  });
